function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
import Decimal from 'decimal.js';
import { relationshipModel } from "../../../models";
import { formatLongMonthDateWithLocalTimezone } from "../../../utils";
import { BillingGroupTypes } from "../genServicesSummary";
const BILLING_GROUP = [BillingGroupTypes.Today, BillingGroupTypes.OnEffectiveDate, BillingGroupTypes.InStages, BillingGroupTypes.Once, BillingGroupTypes.Weekly, BillingGroupTypes.BiWeekly, BillingGroupTypes.Monthly, BillingGroupTypes.Quarterly, BillingGroupTypes.Yearly];
function processServicesInGroup(services, serviceNameToIdMap, servicesInGroupBillingDetails, START_BILLING_AT, now, agreementEffectiveDate) {
  const servicesByStartDate = new Map();
  const servicesWithoutStartDate = [];
  const servicesOnAgreementDate = [];
  const pausedServices = [];
  services.forEach(service => {
    var _servicesInGroupBilli;
    // paused services go into their own bucket
    if (service.isPaused) {
      pausedServices.push(service);
      return;
    }
    const serviceId = serviceNameToIdMap[service.name] || '';
    const priorPeriods = ((_servicesInGroupBilli = servicesInGroupBillingDetails[serviceId]) === null || _servicesInGroupBilli === void 0 ? void 0 : _servicesInGroupBilli.periods) || 0;
    const effectiveStartDate = service.effectiveStartDate;

    // only create separate groups for future dates with no prior periods
    if (effectiveStartDate && priorPeriods === 0 && START_BILLING_AT && effectiveStartDate > now) {
      // check if this date matches the agreement effective date
      const isAgreementDate = agreementEffectiveDate && effectiveStartDate.getTime() === agreementEffectiveDate.getTime();
      if (isAgreementDate) {
        servicesOnAgreementDate.push(service);
      } else {
        // regular dated services grouped by their date
        const startDateKey = effectiveStartDate.getTime().toString();
        if (!servicesByStartDate.has(startDateKey)) {
          servicesByStartDate.set(startDateKey, []);
        }
        servicesByStartDate.get(startDateKey).push(service);
      }
    } else {
      servicesWithoutStartDate.push(service);
    }
  });
  return {
    servicesByStartDate,
    servicesWithoutStartDate,
    servicesOnAgreementDate,
    pausedServices
  };
}
function createBillingGroup(_ref) {
  let {
    group,
    groupType,
    variablePrice,
    services,
    serviceNameToIdMap,
    serviceBillingDetails,
    START_BILLING_AT,
    startDate,
    isPaused
  } = _ref;
  const totalAmount = services.reduce((sum, service) => {
    var _serviceBillingDetail;
    let serviceAmount = service.price;
    const serviceId = serviceNameToIdMap[service.name] || '';
    const priorPeriods = ((_serviceBillingDetail = serviceBillingDetails[serviceId]) === null || _serviceBillingDetail === void 0 ? void 0 : _serviceBillingDetail.periods) || 0;
    if (groupType === BillingGroupTypes.Today && priorPeriods > 0 && START_BILLING_AT) {
      serviceAmount = service.price.times(new Decimal(priorPeriods));
    }
    return sum.plus(serviceAmount);
  }, new Decimal(0));
  return {
    group: _objectSpread(_objectSpread({}, group), {}, {
      services,
      totalAmount,
      startDate
    }),
    groupType,
    variablePrice,
    startDate,
    isPaused
  };
}
export function getBillingGroupTitle(groupType, startDate, relationship, isPaused) {
  // paused services get "upon activation" suffix
  if (isPaused) {
    return "Billed ".concat(groupType, " upon activation");
  }

  // OnEffectiveDate with a date = "Billed on [date]" (no period indication)
  if (groupType === BillingGroupTypes.OnEffectiveDate && startDate) {
    return "Billed on ".concat(formatLongMonthDateWithLocalTimezone(startDate));
  }
  if (groupType === BillingGroupTypes.OnEffectiveDate && relationship) {
    return "Billed ".concat(groupType, " (").concat(relationshipModel.formatEffectiveDate(relationship), ")");
  }
  if (startDate) {
    return "Billed ".concat(groupType, " starting on ").concat(formatLongMonthDateWithLocalTimezone(startDate));
  }
  return "Billed ".concat(groupType);
}
export function processBillingGroups(billingSummary, serviceNameToIdMap, serviceBillingDetails, START_BILLING_AT, now, agreementEffectiveDate) {
  const groups = [];
  const allServicesOnAgreementDate = [];
  BILLING_GROUP.forEach(groupType => {
    [false, true].forEach(variablePrice => {
      var _billingSummary$group;
      const group = (_billingSummary$group = billingSummary[groupType]) === null || _billingSummary$group === void 0 ? void 0 : _billingSummary$group.get(variablePrice);
      if (!group) return;
      const {
        servicesByStartDate,
        servicesWithoutStartDate,
        servicesOnAgreementDate,
        pausedServices
      } = processServicesInGroup(group.services, serviceNameToIdMap, serviceBillingDetails, START_BILLING_AT, now, agreementEffectiveDate);

      // collect agreement date services from all period groups (including InStages)
      allServicesOnAgreementDate.push(...servicesOnAgreementDate);

      // create regular group for services without start date
      // for InStages, this will be services whose first stage is NOT on agreement date
      if (servicesWithoutStartDate.length > 0) {
        groups.push(createBillingGroup({
          group,
          groupType,
          variablePrice,
          services: servicesWithoutStartDate,
          serviceNameToIdMap,
          serviceBillingDetails,
          START_BILLING_AT
        }));
      }

      // create period-specific groups for each start date
      const sortedStartDateEntries = Array.from(servicesByStartDate.entries()).sort((_ref2, _ref3) => {
        let [startDateKeyA] = _ref2;
        let [startDateKeyB] = _ref3;
        return parseInt(startDateKeyA) - parseInt(startDateKeyB);
      });
      sortedStartDateEntries.forEach(_ref4 => {
        let [startDateKey, services] = _ref4;
        const startDate = new Date(parseInt(startDateKey));
        groups.push(createBillingGroup({
          group,
          groupType,
          variablePrice,
          services,
          serviceNameToIdMap,
          serviceBillingDetails,
          START_BILLING_AT,
          startDate
        }));
      });

      // create paused group for paused services
      if (pausedServices.length > 0) {
        groups.push(createBillingGroup({
          group,
          groupType,
          variablePrice,
          services: pausedServices,
          serviceNameToIdMap,
          serviceBillingDetails,
          START_BILLING_AT,
          isPaused: true
        }));
      }
    });
  });

  // create single OnEffectiveDate group for all services billed on agreement date
  if (allServicesOnAgreementDate.length > 0 && agreementEffectiveDate) {
    const totalAmount = allServicesOnAgreementDate.reduce((sum, s) => sum.plus(s.price), new Decimal(0));
    const todayIndex = groups.findIndex(g => g.groupType === BillingGroupTypes.Today);
    groups.splice(todayIndex + 1, 0, {
      groupType: BillingGroupTypes.OnEffectiveDate,
      variablePrice: false,
      startDate: agreementEffectiveDate,
      group: {
        totalAmount,
        services: allServicesOnAgreementDate,
        hasBilledUpfront: true
      }
    });
  }
  return groups;
}